import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  Alert,
  Badge,
  Button,
  Card,
  Group,
  Skeleton,
  Stack,
  Table,
  Text,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { modals } from '@mantine/modals';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/stores/authContext';
import { useIsAgent, useIsManager } from '@/hooks/useRoles';
import { EMPLOYEE_INVITATIONS_QUERY_KEY, useEmployeeInvitations } from '@/hooks/useEmployeeInvitations';
import { IconAlertCircle, IconUserPlus, IconUsers } from '@tabler/icons-react';
import { getApiMessage, getStatus, isNetworkError } from '@/api/errors';
import { userManagementService } from '@/api/services/UserManagementService';
import EmptyState from '@/components/common/EmptyState';
import InviteEmployeeDialog from '@/components/app/company/InviteEmployeeDialog';
import { STATUS } from '@/lib/statusColors';
import { ROUTES } from '@/routes/paths';
import type { EmployeeInvitation, EmployeeInvitationStatus } from '@/types/employee';

/** Цвет статуса: принято — успех, ожидает — внимание, истекло — нейтральное (не ошибка). */
const STATUS_COLOR: Record<EmployeeInvitationStatus | 'REMOVED', string> = {
  ACCEPTED: STATUS.done,
  PENDING: STATUS.waiting,
  EXPIRED: STATUS.neutral,
  REMOVED: STATUS.neutral,
};

/**
 * Сотрудники организации: список приглашений и их состояния.
 *
 * Раздел доступен владельцу-менеджеру аттестованной организации. До аттестации бэкенд приглашения
 * не примет, поэтому запрос не отправляем, а объясняем причину и ведём на вкладку аттестации —
 * «не шлём заведомо отклоняемое действие».
 */
export default function EmployeesTab() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [inviteOpened, setInviteOpened] = useState(false);
  const { logout } = useAuth();
  const isManager = useIsManager();
  // Агентской организации аттестация не требуется — заглушку «сначала аттестуйтесь» ей не показываем.
  const isAgent = useIsAgent();

  const { data, isLoading, error } = useEmployeeInvitations();

  // Организация без аттестации: сервис отклоняет запрос как несоблюдённое условие (406).
  // Агенту это правило не действует, поэтому и заглушку по 406 ему не показываем.
  const notAttested = getStatus(error) === 406 && !isAgent;

  const showError = (cause: unknown) => {
    notifications.show({
      color: 'red',
      message: isNetworkError(cause) ? t('auth.errors.generic') : getApiMessage(cause),
    });
  };

  const inviteMutation = useMutation({
    mutationFn: ({ email, displayName }: { email: string; displayName?: string }) =>
      userManagementService.sendEmployeeInvitation(email, displayName),
    onSuccess: () => {
      notifications.show({ color: 'lime', message: t('app.company.employees.invite.sent') });
      void queryClient.invalidateQueries({ queryKey: EMPLOYEE_INVITATIONS_QUERY_KEY });
    },
    onError: showError,
  });

  const resendMutation = useMutation({
    mutationFn: (id: string) => userManagementService.resendEmployeeInvitation(id),
    onSuccess: () => {
      notifications.show({ color: 'lime', message: t('app.company.employees.resent') });
      void queryClient.invalidateQueries({ queryKey: EMPLOYEE_INVITATIONS_QUERY_KEY });
    },
    onError: showError,
  });

  const removeMutation = useMutation({
    mutationFn: (userId: number) => userManagementService.removeOrganizationMember(userId),
    onSuccess: () => {
      notifications.show({ color: 'lime', message: t('app.company.employees.removed') });
      void queryClient.invalidateQueries({ queryKey: EMPLOYEE_INVITATIONS_QUERY_KEY });
    },
    onError: showError,
  });

  const dissolveMutation = useMutation({
    mutationFn: () => userManagementService.dissolveOrganization(),
    onSuccess: () => {
      // Доступ вызывающего тоже закрыт — выходим сразу, не дожидаясь истечения токена
      notifications.show({ color: 'lime', message: t('app.company.employees.dissolve.done') });
      logout();
    },
    onError: showError,
  });

  const confirmDissolve = () => {
    modals.openConfirmModal({
      title: t('app.company.employees.dissolve.confirmTitle'),
      children: <Text size="sm">{t('app.company.employees.dissolve.confirmText')}</Text>,
      labels: { confirm: t('app.company.employees.dissolve.confirm'), cancel: t('common.cancel') },
      confirmProps: { color: 'red' },
      onConfirm: () => dissolveMutation.mutate(),
    });
  };

  // Исключение необратимо (доступ восстанавливается только новым приглашением) — подтверждаем
  const confirmRemove = (invitation: EmployeeInvitation) => {
    if (invitation.userId == null) {
      return;
    }
    const userId = invitation.userId;
    modals.openConfirmModal({
      title: t('app.company.employees.removeConfirm.title'),
      children: (
        <Text size="sm">
          {t('app.company.employees.removeConfirm.text', {
            name: invitation.displayName || invitation.email,
          })}
        </Text>
      ),
      labels: { confirm: t('app.company.employees.removeConfirm.confirm'), cancel: t('common.cancel') },
      confirmProps: { color: 'red' },
      onConfirm: () => removeMutation.mutate(userId),
    });
  };

  if (isLoading) {
    return (
      <Stack gap="sm">
        <Skeleton height={36} radius="sm" />
        <Skeleton height={120} radius="sm" />
      </Stack>
    );
  }

  if (notAttested) {
    return (
      <Alert
        variant="light"
        color="wheat"
        icon={<IconAlertCircle size={18} />}
        title={t('app.company.employees.locked.title')}
      >
        <Stack gap="xs" align="flex-start">
          <Text size="sm">{t('app.company.employees.locked.text')}</Text>
          <Button component={Link} to={ROUTES.APP_COMPANY_ACCREDITATION} variant="light" size="xs">
            {t('app.company.employees.locked.action')}
          </Button>
        </Stack>
      </Alert>
    );
  }

  const invitations: EmployeeInvitation[] = data ?? [];
  const invite = async (email: string, displayName?: string) => {
    await inviteMutation.mutateAsync({ email, displayName });
  };

  return (
    <Stack gap="md">
      {/* При пустом списке призыв один — в empty-state, кнопку из шапки прячем */}
      {invitations.length > 0 ? (
        <Group justify="space-between">
          <Text fw={600}>{t('app.company.employees.title')}</Text>
          {/* Действия штатом — только владельцу/MANAGER; рядовому сотруднику вкладка read-only (список).
              Вкладка уже скрыта из навигации не-менеджеру, но при прямом заходе по URL кнопки не показываем
              (бэкенд всё равно вернёт 403 — это согласование UI с принципом «остальным read-only»). */}
          {isManager ? (
            <Button leftSection={<IconUserPlus size={16} />} onClick={() => setInviteOpened(true)}>
              {t('app.company.employees.inviteAction')}
            </Button>
          ) : null}
        </Group>
      ) : null}

      {invitations.length === 0 ? (
        <Card withBorder radius="md" p="xl">
          <EmptyState
            decorated={false}
            icon={<IconUsers size={30} />}
            title={t('app.company.employees.empty.title')}
            description={t('app.company.employees.empty.text')}
            action={
              isManager ? (
                <Button leftSection={<IconUserPlus size={16} />} onClick={() => setInviteOpened(true)}>
                  {t('app.company.employees.inviteAction')}
                </Button>
              ) : undefined
            }
          />
        </Card>
      ) : (
        <Card withBorder radius="md" p={0}>
          <Table.ScrollContainer minWidth={520}>
            <Table highlightOnHover verticalSpacing="sm">
              <Table.Thead bg="brand.0">
                <Table.Tr>
                  <Table.Th c="brand.9">{t('app.company.employees.columns.employee')}</Table.Th>
                  <Table.Th c="brand.9">{t('app.company.employees.columns.status')}</Table.Th>
                  <Table.Th c="brand.9" />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {invitations.map((invitation) => {
                  // Принятое приглашение без активного пользователя — сотрудник уже исключён:
                  // findAllByInn не возвращает удалённых, поэтому userId у него пустой. Иначе строка
                  // показывала бы «Работает» у того, кто доступа лишён
                  const removed = invitation.status === 'ACCEPTED' && invitation.userId == null;
                  const displayStatus = removed ? 'REMOVED' : invitation.status;
                  return (
                  <Table.Tr key={invitation.id}>
                    <Table.Td>
                      <Stack gap={2}>
                        <Text size="sm">{invitation.email}</Text>
                        {invitation.displayName ? (
                          <Text size="xs" c="gray.7">
                            {invitation.displayName}
                          </Text>
                        ) : null}
                      </Stack>
                    </Table.Td>
                    <Table.Td>
                      {/* Смысл несут текст и цвет вместе, не только заливка (§9) */}
                      <Badge variant="light" color={STATUS_COLOR[displayStatus]}>
                        {t(`app.company.employees.status.${displayStatus}`)}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      {/* Действия (удалить / отправить повторно) — только менеджеру; иначе столбец пуст (read-only). */}
                      {!isManager ? null : invitation.status === 'ACCEPTED' ? (
                        invitation.userId != null ? (
                          <Button
                            variant="subtle"
                            color="red"
                            size="compact-sm"
                            loading={removeMutation.isPending && removeMutation.variables === invitation.userId}
                            onClick={() => confirmRemove(invitation)}
                          >
                            {t('app.company.employees.remove')}
                          </Button>
                        ) : null
                      ) : (
                        <Button
                          variant="subtle"
                          size="compact-sm"
                          loading={resendMutation.isPending && resendMutation.variables === invitation.id}
                          onClick={() => resendMutation.mutate(invitation.id)}
                        >
                          {t('app.company.employees.resend')}
                        </Button>
                      )}
                    </Table.Td>
                  </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </Card>
      )}

      {isManager ? (
        <Card withBorder radius="md" p="lg" mt="md">
          <Stack gap="xs" align="flex-start">
            <Text fw={600} c="red.7">
              {t('app.company.employees.dissolve.title')}
            </Text>
            <Text size="sm" c="gray.7">
              {t('app.company.employees.dissolve.hint')}
            </Text>
            <Button
              variant="light"
              color="red"
              loading={dissolveMutation.isPending}
              onClick={confirmDissolve}
            >
              {t('app.company.employees.dissolve.action')}
            </Button>
          </Stack>
        </Card>
      ) : null}

      <InviteEmployeeDialog
        opened={inviteOpened}
        onClose={() => setInviteOpened(false)}
        onInvite={invite}
      />
    </Stack>
  );
}
